import React, { useState, useEffect } from 'react';
import { getTaskComments, getTaskActivities, createTaskComment } from '../../../utils/api';
import TaskCommentsSection from './TaskCommentsSection';
import TaskActivitySection from './TaskActivitySection';

const TaskCommentsActivitySection = ({ projectId, taskData, currentUserId, onRefreshTaskData, setRefreshTaskActivitiesData, refreshTaskActivitiesData }) => {
    const [activeTab, setActiveTab] = useState('comments'); // 'comments' or 'activity'
    const [sortOrder, setSortOrder] = useState('oldest');   // 'oldest' or 'newest'
    const [taskComments, setTaskComments] = useState([]);
    const [taskActivities, setTaskActivities] = useState([]);

    const currentTask = taskData;

    const formatRelativeTime = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            const now = new Date();
            const diffMs = now - date;
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMs / 3600000);
            const diffDays = Math.floor(diffMs / 86400000);

            if (diffMins < 1) return 'just now';
            if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
            if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
            if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        } catch (e) {
            return '';
        }
    };

    useEffect(() => {
        if (refreshTaskActivitiesData) {
            setRefreshTaskActivitiesData(false);    
            refreshTaskActivities();
        }
    }, [refreshTaskActivitiesData]);

    const refreshTaskComments = async () => {
        if (!projectId || !currentTask?.id) return;
        try {
            const response = await getTaskComments(projectId, currentTask.id);
            if (response && response.data) {
                setTaskComments(response.data);
                if(typeof onRefreshTaskData === "function") {
                    onRefreshTaskData();
                }
            }
        } catch (err) {
            console.error('Failed to refresh task comments:', err);
        }
    };

    const refreshTaskActivities = async () => {
        if (!projectId || !currentTask?.id) return;
        try {
            const response = await getTaskActivities(projectId, currentTask.id);
            if (response && response.data) {
                setTaskActivities(response.data);
            }
        } catch (err) {
            console.error('Failed to refresh task activities:', err);
        }
    };

    useEffect(() => {
        if (projectId && currentTask?.id) {
            refreshTaskComments();
            refreshTaskActivities();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId, currentTask?.id]);

    const handleAddComment = async (commentData) => {
        if (
            !commentData ||
            (!commentData.content &&
                (!commentData.attachments || commentData.attachments.length === 0)) ||
            !projectId ||
            !currentTask?.id
        ) {
            return;
        }

        try {
            const hasAttachments =
                Array.isArray(commentData.attachments) && commentData.attachments.length > 0;

            const formData = new FormData();
            const content = commentData.content || '';
            const htmlContent = commentData.htmlContent || commentData.content || '';
            const mentions = commentData.mentions || [];

            formData.append('content', content);
            formData.append('html_content', htmlContent);

            if (mentions.length > 0) {
                formData.append('mentions', JSON.stringify(mentions));
            }

            if (hasAttachments) {
                commentData.attachments.forEach((att, index) => {
                    const file = att?.file || att;
                    if (!file) return;
                    const name = file.name || `attachment-${index}`;
                    formData.append('attachments[]', file, name);
                });
            }

            const response = await createTaskComment(projectId, currentTask.id, formData);
            if (response && response.data) {
                await refreshTaskComments();
                await refreshTaskActivities(); // Refresh activities after adding comment
            }
        } catch (err) {
            console.error('Failed to add comment:', err);
            alert('Failed to add comment. Please try again.');
        }
    };

    if (!currentTask) {
        return null;
    }

    const creatorId = currentTask?.created_by || currentTask?.creator_id;
    const creator = taskData?.project_users?.find((u) => (u.ID || u.id) == creatorId);
    const createdDate =
        currentTask?.created_at || currentTask?.created_on || currentTask?.created_date;
    const creatorName = creator ? creator.display_name || creator.name : 'Unknown';
    const createdTimeAgo = formatRelativeTime(createdDate);

    const sortedComments =
        sortOrder === 'oldest' ? taskComments : [...taskComments].reverse();
    const sortedActivities =
        sortOrder === 'oldest' ? [...taskActivities].reverse() : taskActivities;

    return (
        <div className="task-detail-comments-section">
            <div className="task-detail-comments-header">
                <div className="task-detail-tabs">
                    <span
                        className={`task-detail-tab ${
                            activeTab === 'comments' ? 'active' : ''
                        }`}
                        onClick={() => setActiveTab('comments')}
                    >
                        Comments
                    </span>
                    <span
                        className={`task-detail-tab ${
                            activeTab === 'activity' ? 'active' : ''
                        }`}
                        onClick={() => setActiveTab('activity')}
                    >
                        All activity
                    </span>
                </div>
                <button
                    className="task-detail-sort-btn"
                    onClick={() =>
                        setSortOrder(sortOrder === 'oldest' ? 'newest' : 'oldest')
                    }
                >
                    <i
                        className={`fa-solid fa-arrow-${
                            sortOrder === 'oldest' ? 'up' : 'down'
                        }`}
                    ></i>
                    {sortOrder === 'oldest' ? 'Oldest' : 'Newest'}
                </button>
            </div>

            {activeTab === 'comments' ? (
                <TaskCommentsSection
                    taskComments={sortedComments}
                    formatRelativeTime={formatRelativeTime}
                    taskData={taskData}
                    currentUserId={currentUserId}
                    onAddComment={handleAddComment}
                    projectId={projectId}
                    taskId={currentTask?.id}
                    onRefreshComments={refreshTaskComments}
                />
            ) : (
                <TaskActivitySection
                    formatRelativeTime={formatRelativeTime}
                    createdDate={createdDate}
                    creatorName={creatorName}
                    createdTimeAgo={createdTimeAgo}
                    creator={creator}
                    taskActivities={sortedActivities}
                />
            )}
        </div>
    );
};

export default TaskCommentsActivitySection;


